/**
 * AccouPro API Client
 * Unified client for mobile and web apps to interact with the AccouPro backend.
 */

import { supabase } from "@/integrations/supabase/client";

const API_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mobile-api`;

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  return headers;
}

async function apiCall<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
    return { success: true, data };
  } catch (err: any) {
    return { success: false, error: err.message || "Network error" };
  }
}

// ── Invoices API ────────────────────────────────────────────────

export const invoicesApi = {
  list: (params?: { type?: string; status?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams(params as any).toString();
    return apiCall("GET", `/invoices${query ? `?${query}` : ""}`);
  },
  get: (id: string) => apiCall("GET", `/invoices/${id}`),
  create: (invoice: { type: string; party_name: string; amount: number; lines: any[] }) =>
    apiCall("POST", "/invoices", invoice),
  createPaymentLink: (id: string, opts: { gateway?: string; expires_days?: number }) =>
    apiCall("POST", `/invoices/${id}/payment-link`, opts),
};

// ── Payments API ────────────────────────────────────────────────

export const paymentsApi = {
  list: (params?: { status?: string; from?: string; to?: string }) => {
    const query = new URLSearchParams(params as any).toString();
    return apiCall("GET", `/payments${query ? `?${query}` : ""}`);
  },
  get: (id: string) => apiCall("GET", `/payments/${id}`),
  record: (payment: { invoice_id: string; amount: number; method?: string }) =>
    apiCall("POST", "/payments", payment),
  refund: (id: string, reason?: string) =>
    apiCall("POST", `/payments/${id}/refund`, { reason }),
  reconcile: (id: string) =>
    apiCall("POST", `/payments/${id}/reconcile`),
};

// ── GST API ─────────────────────────────────────────────────────

export const gstApi = {
  returns: (period: string) => apiCall("GET", `/gst/returns?period=${period}`),
  validateGSTIN: (gstin: string) => apiCall("GET", `/gst/validate/${gstin}`),
  generateEInvoice: (invoiceId: string) =>
    apiCall("POST", "/gst/e-invoice", { invoice_id: invoiceId }),
  generateEWayBill: (invoiceId: string) =>
    apiCall("POST", "/gst/eway-bill", { invoice_id: invoiceId }),
};

// ── Reports API ─────────────────────────────────────────────────

export const reportsApi = {
  trialBalance: (asOf?: string) =>
    apiCall("GET", `/reports/trial-balance${asOf ? `?as_of=${asOf}` : ""}`),
  profitLoss: (from?: string, to?: string) =>
    apiCall("GET", `/reports/profit-loss?from=${from || ""}&to=${to || ""}`),
  balanceSheet: (asOf?: string) =>
    apiCall("GET", `/reports/balance-sheet${asOf ? `?as_of=${asOf}` : ""}`),
  cashFlow: (period?: string) =>
    apiCall("GET", `/reports/cash-flow?period=${period || "monthly"}`),
  receivablesAging: () => apiCall("GET", "/reports/receivables-aging"),
};

// ── Users API ───────────────────────────────────────────────────

export const usersApi = {
  me: () => apiCall("GET", "/users/me"),
  profile: () => apiCall("GET", "/users/profile"),
  updateProfile: (data: Record<string, any>) => apiCall("PUT", "/users/profile", data),
  preferences: () => apiCall("GET", "/users/preferences"),
  updatePreferences: (data: Record<string, any>) => apiCall("PUT", "/users/preferences", data),
};

// ── Health Check ────────────────────────────────────────────────

export const healthCheck = () => apiCall("GET", "/health");

// ── Full API namespace ──────────────────────────────────────────

export const accouProApi = {
  invoices: invoicesApi,
  payments: paymentsApi,
  gst: gstApi,
  reports: reportsApi,
  users: usersApi,
  health: healthCheck,
};
